// Main class to handle Trustpilot reviews display and functionality
class TrustpilotReviews {
  constructor(config) {
    this.businessUnitId = config.businessUnitId;
    this.apiKey = config.apiKey;
    this.container = document.querySelector('.reviews-slider__list');
    this.reviewsPerPage = config.reviewsPerPage;
    this.minRating = config.minRating;
    this.characterLimit = config.characterLimit;
    this.currentPage = 1;
    this.slider = null;
    this.summaryContainer = document.querySelector('.js-reviews-summary');
    this.starIconUrl = config.starIconUrl;
    this.badgeIconUrl = config.badgeIconUrl;
    this.trustpilotLogoUrl = config.trustpilotLogoUrl;
    this.translations = (window.translations && window.translations.trustpilot) || {};
  }

  // Fetch reviews from Trustpilot API
  async fetchReviews(page = 1) {
    try {
      const starsParam = Array.from({ length: 6 - this.minRating }, (_, i) => i + this.minRating).join(',');

      const response = await fetch(
        `https://api.trustpilot.com/v1/business-units/${this.businessUnitId}/reviews?page=${page}&perPage=${this.reviewsPerPage}&stars=${starsParam}`,
        {
          headers: {
            ApiKey: this.apiKey,
          },
        }
      );
      const data = await response.json();
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console -- Trustpilot error fetching reviews
      console.error('Error fetching reviews:', error);
      return null;
    }
  }

  formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  // Create and return star rating element
  renderStars(rating) {
    const container = document.createElement('div');
    container.className = 'star-rating';

    const starsSpan = document.createElement('span');
    starsSpan.className = 'star-rating__stars';

    const filledSpan = document.createElement('span');
    filledSpan.className = 'star-rating__filled';

    for (let i = 0; i < rating; i++) {
      const img = document.createElement('img');
      img.src = this.starIconUrl;
      img.alt = this.translations.starAlt || 'Star';
      img.width = 20;
      img.height = 21;
      filledSpan.appendChild(img);
    }

    const numberSpan = document.createElement('span');
    numberSpan.className = 'star-rating__number';
    numberSpan.textContent = rating;

    starsSpan.appendChild(filledSpan);
    container.appendChild(starsSpan);
    container.appendChild(numberSpan);

    return container;
  }

  // Create and return individual review element
  renderReview(review) {
    // Format display name
    const nameParts = review.consumer.displayName.split(' ').filter((part) => part !== '.');

    let formattedName;
    if (nameParts.length === 1) {
      formattedName = nameParts[0];
    } else {
      const isFirstPartSurname = nameParts[0] === nameParts[0].toUpperCase() && nameParts[0].length > 1;
      if (isFirstPartSurname) {
        formattedName = `${nameParts[0][0]}. ${nameParts[1]}`;
      } else {
        formattedName = nameParts[1] ? `${nameParts[0]} ${nameParts[1][0]}.` : nameParts[0];
      }
    }

    const slide = document.createElement('li');
    slide.className = 'splide__slide';

    const card = document.createElement('div');
    card.className = 'review-card';

    const reviewSection = document.createElement('div');
    reviewSection.className = 'review-card-review';

    const header = document.createElement('div');
    header.className = 'review-header';

    const starsDiv = document.createElement('div');
    starsDiv.className = 'review-stars';
    starsDiv.appendChild(this.renderStars(review.stars));

    const title = document.createElement('h3');
    title.className = 'review-title h5';
    title.textContent = review.title;

    const content = document.createElement('p');
    content.className = 'review-content';
    if (review.text.length > this.characterLimit && this.characterLimit > 0) {
      content.textContent = `${review.text.slice(0, this.characterLimit)}...`;
    } else {
      content.textContent = review.text;
    }

    const authorSection = document.createElement('div');
    authorSection.className = 'review-card-author';

    const author = document.createElement('div');
    author.className = 'review-author';

    const authorName = document.createElement('span');
    authorName.className = 'author-name';
    authorName.textContent = formattedName;

    const verified = document.createElement('span');
    verified.className = 'author-verified';

    const verifiedIcon = document.createElement('img');
    verifiedIcon.src = this.badgeIconUrl;
    verifiedIcon.alt = '';
    verifiedIcon.width = 24;
    verifiedIcon.height = 24;

    header.appendChild(starsDiv);
    reviewSection.appendChild(header);
    reviewSection.appendChild(title);
    reviewSection.appendChild(content);

    verified.appendChild(verifiedIcon);
    verified.appendChild(document.createTextNode(this.translations.verifiedBuyer || 'Verified buyer'));

    author.appendChild(authorName);
    author.appendChild(verified);
    authorSection.appendChild(author);

    card.appendChild(reviewSection);
    card.appendChild(authorSection);
    slide.appendChild(card);

    return slide;
  }

  // Initialise Splide slider with configuration
  initSlider() {
    if (this.slider) {
      this.slider.destroy();
    }

    const sliderComponent = document.querySelector('.js-trustpilot-slider');
    if (!sliderComponent) return;

    this.slider = new window.Splide('.js-trustpilot-splide', {
      perPage: 4,
      perMove: 1,
      pagination: false,
      gap: '2.4rem',
      bound: true,
      rewind: false,
      arrows: true,
      breakpoints: {
        1278: {
          perPage: 3,
          perMove: 1,
          gap: '1.6rem',
        },
        989: {
          perPage: 2,
          padding: { left: 40, right: 40 },
          perMove: 1,
          gap: '1.6rem',
        },
        749: {
          perPage: 1,
          padding: { left: 16, right: 16 },
          gap: '0.8rem',
          perMove: 1,
        },
      },
    }).mount();

    const sliderContainer = document.querySelector('.js-trustpilot-splide');
    const allPrevButtons = sliderContainer.querySelectorAll('.splide__arrow--prev');
    const allNextButtons = sliderContainer.querySelectorAll('.splide__arrow--next');

    allPrevButtons.forEach((button) => {
      button.addEventListener('click', () => this.slider.go('<'));
    });

    allNextButtons.forEach((button) => {
      button.addEventListener('click', () => this.slider.go('>'));
    });

    this.slider.on('moved', () => {
      const isBeginning = this.slider.index === 0;
      const isEnd = this.slider.index === this.slider.length - this.slider.options.perPage;

      allPrevButtons.forEach((button) => {
        button.disabled = isBeginning;
      });

      allNextButtons.forEach((button) => {
        button.disabled = isEnd;
      });
    });

    sliderComponent.style.visibility = 'visible';
  }

  // Fetch and display reviews
  async loadReviews(page = 1) {
    const data = await this.fetchReviews(page);
    if (!data) return;

    this.container.innerHTML = '';

    const filteredReviews = data.reviews.filter((review) => review.stars >= this.minRating);
    filteredReviews.forEach((review) => {
      const reviewElement = this.renderReview(review);
      this.container.appendChild(reviewElement);
    });

    this.initSlider();
  }

  // Fetch business information from Trustpilot
  async fetchBusinessInfo() {
    try {
      const response = await fetch(`https://api.trustpilot.com/v1/business-units/${this.businessUnitId}`, {
        headers: {
          ApiKey: this.apiKey,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console -- Trustpilot error fet
      console.error('Error fetching business info:', error);
      return null;
    }
  }

  // Create and display business summary element
  renderSummary(businessInfo) {
    try {
      const trustScore = businessInfo.score.trustScore;
      const reviewCount = businessInfo.numberOfReviews.total;

      if (!trustScore || !reviewCount) {
        // eslint-disable-next-line no-console -- Trustpilot error fetching business info
        console.error('Missing required business info data');
        return;
      }

      const ratingDiv = document.createElement('div');
      ratingDiv.className = 'reviews-summary__rating';

      const ratingSpan = document.createElement('span');

      ratingSpan.appendChild(document.createTextNode(`${this.translations.rated} ` || `Rated `));

      const scoreSpan = document.createElement('span');
      scoreSpan.className = 'reviews-summary__bold';
      scoreSpan.textContent = `${trustScore.toFixed(1)}/5`;
      ratingSpan.appendChild(scoreSpan);

      ratingSpan.appendChild(document.createTextNode(`${this.translations.basedOn} ` || ` based on `));

      const countSpan = document.createElement('span');
      countSpan.className = 'reviews-summary__bold';
      countSpan.textContent = `${reviewCount.toLocaleString()} ${this.translations.reviews || 'reviews'}`;
      ratingSpan.appendChild(countSpan);

      ratingDiv.appendChild(ratingSpan);

      const logoDiv = document.createElement('div');
      logoDiv.className = 'reviews-summary__logo';

      const logoImg = document.createElement('img');
      logoImg.src = this.trustpilotLogoUrl;
      logoImg.alt = 'Trustpilot';
      logoImg.loading = 'lazy';

      logoDiv.appendChild(logoImg);

      if (this.summaryContainer) {
        this.summaryContainer.innerHTML = '';
        this.summaryContainer.appendChild(ratingDiv);
        this.summaryContainer.appendChild(logoDiv);
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- Trustpilot error rendering summary
      console.error('Error rendering summary:', error);
    }
  }

  // Initialise reviews display
  async init() {
    const businessInfo = await this.fetchBusinessInfo();
    if (businessInfo) {
      this.renderSummary(businessInfo);
    }
    this.loadReviews();
  }
}

// Start reviews component when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const config = window.trustpilotConfig || {};
  const trustpilotReviews = new TrustpilotReviews(config);
  trustpilotReviews.init();
});
